import { useParams, Link } from "react-router-dom";
import products from "../data/products.generated.json";
import { BRAND_MAP } from "../data/brandMap";
import { useState, useEffect } from "react";
import { generateSummary } from "../Components/Wizard/generateSummary";

const HIDDEN_VALUES = [
  "メーカー記載なし",
  "記載なし",
  "未記載",
  "不明",
  "なし",
  "-",
  "",
  null,
  undefined,
];

const DIAGNOSIS_LABELS = {
  mountType: "設置場所",
  connectivity: "通信方法",
  recordingMethod: "録画方法",
  lensType: "撮影範囲",
  waterproofDustproof: "防水・防塵",
  resolution: "画質",
  lowLightPerformance: "夜間撮影",
  aiFeatures: "AI機能",
};

const DETAIL_LABELS = {
  mountType: "設置タイプ",
  connectivity: "接続方式",
  recordingMethod: "録画方式",
  lensType: "レンズ情報",
  waterproofDustproof: "防水・防塵性能",
  resolution: "解像度",
  lowLightPerformance: "低照度性能",
  aiFeatures: "AI機能",
};

function isVisibleValue(value) {
  if (Array.isArray(value)) return value.length > 0;
  return !HIDDEN_VALUES.includes(value);
}

export default function BrandProducts() {
  const { brandSlug } = useParams();
  const makerName = BRAND_MAP[brandSlug];

  useEffect(() => {
    document.title = makerName
      ? `${makerName}の防犯カメラ一覧｜CAMARAGO`
      : "メーカーが見つかりません｜CAMARAGO";
  }, [makerName]);

  if (!makerName) {
    return (
      <main className="min-h-screen px-5 py-10 bg-[#031259] text-white">
        <div className="max-w-4xl mx-auto bg-white/10 border border-white/10 rounded-3xl p-8 shadow">
          <h1 className="text-2xl font-bold mb-3">メーカーが見つかりません</h1>
          <p className="text-blue-100">指定されたメーカー情報は現在登録されていません。</p>
        </div>
      </main>
    );
  }

  const brandProducts = products.filter((product) => product.makerName === makerName);

  return (
    <main className="min-h-screen px-5 py-10 bg-[#031259] text-white">
      <div className="max-w-7xl mx-auto">
        <section className="mb-10">
          <p className="text-sm text-blue-300 font-semibold mb-3">CAMARAGO掲載情報確認ページ</p>

          <h1 className="text-4xl md:text-5xl font-black mb-5 tracking-tight">
            {makerName} の防犯カメラ一覧
          </h1>

          <p className="text-blue-100 leading-relaxed mb-2">
            現在、CAMARAGOでは {brandProducts.length} 件の{makerName}
            製品を掲載しています。
          </p>

          <p className="text-blue-100 leading-relaxed mb-8">
            「CAMARAGO診断結果」と「カメラ詳細情報」を切り替えて確認できます。
          </p>

          <Link
            to="/wizard"
            className="inline-flex items-center gap-2 bg-white text-[#031259] px-6 py-4 rounded-xl font-bold shadow-lg hover:translate-y-[-2px] transition"
          >
            商品が多くて選べない方は診断する
            <span>→</span>
          </Link>
        </section>

        <section className="grid gap-8">
          {brandProducts.map((product) => (
            <ProductCard key={product.modelNumber || product.productName} product={product} />
          ))}
        </section>

        <p className="text-xs text-blue-200 mt-10">
          ※ CAMARAGO診断結果は、製品情報をもとに一般ユーザー向けに整理した表示です。
        </p>
      </div>
    </main>
  );
}

function ProductCard({ product }) {
  const [mode, setMode] = useState("diagnosis");
  const features = product.features ?? {};
  const summary = generateSummary(product);

  return (
    <article className="bg-white text-slate-900 rounded-3xl p-6 md:p-8 shadow-xl border border-white/20">
      <div className="grid gap-6 lg:grid-cols-[1fr_1.4fr] lg:items-start">
        <div>
          {product.makerName && (
            <div className="mb-4 inline-flex items-center rounded-full bg-blue-50 border border-blue-100 px-4 py-2 text-sm font-bold text-[#002b5b]">
              {product.makerName}
            </div>
          )}

          <h2 className="text-2xl md:text-3xl font-black leading-snug mb-4">
            {product.productName || "商品名未登録"}
          </h2>

          <p className="inline-flex items-center gap-2 text-slate-600 font-semibold">
            <span className="rounded-lg bg-slate-100 px-3 py-1 text-[#031259]">型番</span>
            {product.modelNumber || "未登録"}
          </p>

          <button
            onClick={() => setMode(mode === "diagnosis" ? "detail" : "diagnosis")}
            className="mt-6 inline-flex items-center justify-center bg-[#031259] text-white rounded-full w-12 h-12 shadow-lg hover:scale-105 transition"
            aria-label="表示を切り替える"
          >
            ⇄
          </button>

          <p className="text-sm text-slate-500 mt-3">クリックで表示を切り替え</p>
        </div>

        <div className="perspective-[1200px]">
          <div
            className={`relative transition-transform duration-700 [transform-style:preserve-3d] ${
              mode === "detail" ? "[transform:rotateY(180deg)]" : ""
            }`}
          >
            <CardFace
              title="CAMARAGO診断結果"
              labels={DIAGNOSIS_LABELS}
              values={summary}
              isBack={false}
            />

            <CardFace
              title="カメラ詳細情報"
              labels={DETAIL_LABELS}
              values={features}
              isBack={true}
            />
          </div>
        </div>
      </div>

      <div className="mt-6 flex rounded-2xl bg-slate-100 p-1 text-sm">
        <button
          onClick={() => setMode("diagnosis")}
          className={`flex-1 px-3 py-3 rounded-xl font-bold transition ${
            mode === "diagnosis" ? "bg-white shadow text-[#031259]" : "text-slate-500"
          }`}
        >
          CAMARAGO診断結果
        </button>

        <button
          onClick={() => setMode("detail")}
          className={`flex-1 px-3 py-3 rounded-xl font-bold transition ${
            mode === "detail" ? "bg-white shadow text-[#031259]" : "text-slate-500"
          }`}
        >
          カメラ詳細情報
        </button>
      </div>
    </article>
  );
}

function CardFace({ title, labels, values, isBack }) {
  return (
    <div
      className={`rounded-3xl bg-slate-50 border border-slate-100 p-5 md:p-6 shadow-inner h-[620px] overflow-y-auto ${
        isBack
          ? "absolute inset-0 [transform:rotateY(180deg)] [backface-visibility:hidden]"
          : "[backface-visibility:hidden]"
      }`}
    >
      <div className="mb-5 text-center">
        <span className="inline-flex rounded-full bg-[#031259] text-white px-4 py-2 text-sm font-bold">
          {title}
        </span>
      </div>

      <div className="grid gap-3">
        {Object.entries(labels).map(([key, label]) => {
          const value = values[key];

          if (!isVisibleValue(value)) return null;

          return (
            <div
              key={key}
              className="rounded-2xl bg-white px-4 py-3 text-sm md:text-base text-slate-700"
            >
              <div className="font-black text-[#031259] mb-1">{label}</div>
              <div className="font-semibold leading-relaxed break-words">
                {Array.isArray(value) ? value.join("、") : value}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
